import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import {FaPause, FaPlay, FaVolumeDown, FaVolumeMute, FaVolumeUp} from "react-icons/fa";

const BAR_COUNT = 200;
const BAR_MAX_HEIGHT = 32;
const SEEK_STEP = 5;
const PLACEHOLDER_PEAKS = Array.from({length: BAR_COUNT}, () => 0.18);

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return "0:00";

    return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
};

const buildPeaks = (audioBuffer) => {
    const samples = audioBuffer.getChannelData(0);
    const blockSize = Math.floor(samples.length / BAR_COUNT) || 1;

    const peaks = Array.from({length: BAR_COUNT}, (_, index) => {
        const start = index * blockSize;
        let sum = 0;

        for (let offset = 0; offset < blockSize; offset++) sum += Math.abs(samples[start + offset] ?? 0);

        return sum / blockSize;
    });

    const loudest = Math.max(...peaks);

    return loudest > 0 ? peaks.map((peak) => peak / loudest) : peaks;
};

const WaveformAudioPlayer = ({src, className = ""}) => {
    const audioRef = useRef(null);
    const [playableSrc, setPlayableSrc] = useState(null);
    const [peaks, setPeaks] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [volumeOpen, setVolumeOpen] = useState(false);

    useEffect(() => {
        let active = true;
        let objectUrl = null;

        setPlayableSrc(null);
        setPeaks(null);
        setCurrentTime(0);
        setDuration(0);

        if (!src) return;

        const decodePeaks = (buffer) => {
            const OfflineContext = window.OfflineAudioContext ?? window.webkitOfflineAudioContext;
            if (!OfflineContext) return;

            new OfflineContext(1, 1, 44100).decodeAudioData(buffer)
                .then((audioBuffer) => { if (active) setPeaks(buildPeaks(audioBuffer)); })
                .catch((error) => console.error(error));
        };

        axios.get(src, {responseType: "arraybuffer"})
            .then(({data, headers}) => {
                if (!active) return;

                objectUrl = URL.createObjectURL(new Blob([data], {type: headers["content-type"] || "audio/mpeg"}));

                setPlayableSrc(objectUrl);
                decodePeaks(data);
            })
            .catch((error) => {
                console.error(error);
                if (active) setPlayableSrc(src);
            });

        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [src]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.volume = volume;
        audio.muted = muted;
    }, [volume, muted, playableSrc]);

    const changeVolume = (value) => {
        setVolume(value);
        setMuted(value === 0);
    };

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) audio.play().catch((error) => console.error(error));
        else audio.pause();
    };

    const seekTo = (seconds) => {
        const audio = audioRef.current;
        const total = audio?.duration;
        if (!audio || !Number.isFinite(total) || !total) return;

        audio.currentTime = Math.min(Math.max(seconds, 0), total);
        setCurrentTime(audio.currentTime);
    };

    const seekFromClick = (event) => {
        const total = audioRef.current?.duration;
        if (!Number.isFinite(total)) return;

        const {left, width} = event.currentTarget.getBoundingClientRect();

        seekTo(((event.clientX - left) / width) * total);
    };

    const seekFromKey = (event) => {
        if (event.key === "ArrowRight") seekTo(currentTime + SEEK_STEP);
        else if (event.key === "ArrowLeft") seekTo(currentTime - SEEK_STEP);
        else return;

        event.preventDefault();
    };

    const bars = peaks ?? PLACEHOLDER_PEAKS;
    const progress = duration ? currentTime / duration : 0;
    const VolumeIcon = (muted || volume === 0) ? FaVolumeMute : (volume < 0.5 ? FaVolumeDown : FaVolumeUp);
    const volumePercent = Math.round((muted ? 0 : volume) * 100);

    if (!src) return <span className="block w-full truncate text-sm text-gray-500">Sem gravação</span>;

    return (
        <div className={`flex min-w-0 items-center gap-3 bg-[#f1f3f4] rounded-3xl px-5 py-2 ${className}`.trim()}>
            <audio
                ref={audioRef}
                src={playableSrc || undefined}
                preload="metadata"
                onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
                onDurationChange={(event) => setDuration(event.currentTarget.duration)}
                onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                className="hidden"
            />

            <button
                type="button"
                onClick={togglePlay}
                disabled={!playableSrc}
                aria-label={playing ? "Pausar gravação" : "Reproduzir gravação"}
                className="flex shrink-0 cursor-pointer items-center justify-center rounded-full hover:text-[#1a4585] disabled:cursor-default disabled:opacity-60"
            >
                {playing ? <FaPause size={18}/> : <FaPlay size={18}/>}
            </button>

            <div className="flex min-w-0 flex-1 flex-row items-baseline gap-1.5">
                <span className={"text-xs text-[#7a838f] "}>{formatTime(currentTime)}</span>
                <button
                    type="button"
                    onClick={seekFromClick}
                    onKeyDown={seekFromKey}
                    aria-label="Posição da gravação"
                    className="flex h-8 w-full cursor-pointer items-center gap-px"
                >
                    {bars.map((peak, index) => (
                        <span
                            key={index}
                            className="flex-1 rounded-full"
                            style={{
                                height: `${Math.max(2, Math.round(peak * BAR_MAX_HEIGHT))}px`,
                                backgroundColor: index / BAR_COUNT < progress ? "#14376b" : "#c9cfd8",
                            }}
                        />
                    ))}
                </button>

                <span className={"text-xs text-[#7a838f] "}>{formatTime(duration)}</span>
            </div>

            <div
                className="relative shrink-0"
                onMouseEnter={() => setVolumeOpen(true)}
                onMouseLeave={() => setVolumeOpen(false)}
                onFocus={() => setVolumeOpen(true)}
                onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setVolumeOpen(false); }}
            >
                <button
                    type="button"
                    onClick={() => setMuted(!muted)}
                    aria-label={muted ? "Reativar som" : "Silenciar"}
                    className="flex cursor-pointer items-baseline justify-center text-[#5a6675] hover:text-[#14376b]"
                >
                    <VolumeIcon size={18}/>
                </button>

                {volumeOpen && (
                    <div className="absolute bottom-full left-1/2 z-[1000] -translate-x-1/2 pb-2">
                        <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-lg">
                            <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.05}
                                value={muted ? 0 : volume}
                                onChange={(event) => changeVolume(Number(event.target.value))}
                                aria-label="Volume"
                                className="h-1 w-24 cursor-pointer accent-[#14376b]"
                            />

                            <span className="w-8 text-right text-xs text-[#7a838f] tabular-nums">{volumePercent}%</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WaveformAudioPlayer;
